"use client";

import { useNavigate, useParams } from "react-router-dom";

import { FormColumns, FormLabel } from "components/form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "ui/components/form";
import AmountWithCurrencyInput from "components/form/amount-currency";
import ButtonWithLoading from "@/components/button-with-loading";
import { DatePicker } from "components/date-picker-2";
import { Form } from "react-crud-form";
import { Input } from "ui/components/input";
import { PermissionGate } from "permissions/components/gate";
import { Portal } from "@radix-ui/react-portal";

import classNames from "classnames";
import { bucketJobConfigSchema } from "@/modules/work/jobs/config/bucket/schema";
import { useCreateBucketConfig, useUpdateBucketConfig } from "@/modules/work/jobs/config/bucket/mutation";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";

import { zodResolver } from "@hookform/resolvers/zod";
import { PermissionControlledInput } from "permissions/components/input";
import { FormActions } from "components/form/actions";

const BucketConfigForm = ({ config }) => {
  const navigate = useNavigate();

  const { jobId } = useParams();

  const form = useForm({
    resolver: zodResolver(bucketJobConfigSchema),
    defaultValues: config || {},
  });

  const createMutation = useCreateBucketConfig();
  const updateMutation = useUpdateBucketConfig();

  const handleCreate = async ({ values }) => {
    await createMutation.mutateAsync({
      jobId,
      values,
    });

    navigate(-1);
  };

  const handleUpdate = async (id, context) => {
    const { dirtyValues } = context;
    await updateMutation.mutateAsync({
      id,
      values: dirtyValues,
    });
    navigate(-1);
  };

  return (
    <Form
      form={form}
      create={handleCreate}
      update={handleUpdate}
      itemId={config?.id || "+"}
    >
      {({ isCreating, createItem, isUpdating, updateItem, isDirty, isNewItem }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (isNewItem) {
              createItem();
            } else {
              updateItem();
            }
          },
          { preventDefault: true },
        );

        return (
          <div className={classNames("p-6 max-w-md space-y-6")}>
            <AmountWithCurrencyInput
              form={form}
              amountKey="amount"
              amountLabel="Amount"

              collection="bucket_job_config"
              isNewItem={isNewItem}
            />

            <FormField
              control={form.control}
              name="hours"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                    collection="bucket_job_config"
                    field={field.name}
                    type="form"
                  >
                    <FormLabel required={true}>Hours</FormLabel>

                    <Input
                      type="number"
                      step="0.5"

                      {...field}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : null,
                        )
                      }
                    />
                  </PermissionControlledInput>
                  <FormDescription>
                    Total number of hours allocated for this bucket.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormActions
              rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
              collection="bucket_job_config"
              isNewItem={isNewItem}
              isCreating={isCreating}
              isUpdating={isUpdating}
              createItem={createItem}
              updateItem={updateItem}
              isDirty={isDirty}
            />
          </div>
        );
      }}
    </Form>
  );
};

export default BucketConfigForm;
